import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, useState } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import DateRangeDatePicker from "../../components/form/formDatePicker/DateRangeDatePicker";
import FormDropdown from "../../components/form/FormDropdown";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorRow from "../../components/inspector/InspectorRow";
import ModalField from "../../components/modal/ModalField";
import PopoverResizableBody from "../../components/popover/PopoverResizableBody";
import ToolbarDropdownButton from "../../components/toolbar/ToolbarDropdownButton";
import ToolbarGroup from "../../components/toolbar/ToolbarGroup";
import ToolbarIconButton from "../../components/toolbar/ToolbarIconButton";
import ToolbarPopoverButton from "../../components/toolbar/ToolbarPopoverButton";
import ToolbarPopoverList from "../../components/toolbar/ToolbarPopoverList";
import ToolbarSlider from "../../components/toolbar/ToolbarSlider";
import ToolbarSpacer from "../../components/toolbar/ToolbarSpacer";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
import ToolbarView from "../../components/toolbar/ToolbarView";
import TreeListView from "../../components/treeList/TreeListView";
import checkboxStates from "../../constants/checkboxStates";
import { DateFormatIdentifiers } from "../../constants/dateFormats";
import fontSizes from "../../constants/fontSizes";
import {
  MENU_POPOVER_DATE,
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  RELATIONSHIP_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import itemCardDetailRows from "../../constants/itemCardDetailRows";
import narrativeColumnLevels, {
  descriptionForNarrativeColumnLevel,
  labelForNarrativeColumnLevel,
  shortLabelForNarrativeColumnLevel
} from "../../constants/narrativeColumnLevels";
import specialSyntax from "../../constants/specialSyntax";
import subwayDisplayOptions, {
  descriptionForSubwayLayout,
  iconForSubwayLayout,
  labelForSubwayLayout
} from "../../constants/subwayDisplayOptions";
import subwayTextLineCountOptions, {
  iconForSubwayTextLineCount,
  labelForSubwayTextLineCount
} from "../../constants/subwayTextLineCountOptions";
import { getOutOfRangeText } from "../../constants/timelineDateRange";
import { TIMELINE_GROUP_SHOW_ALL } from "../../constants/timelineGroupOptions";
import timelineLayoutOptions, {
  descriptionForTimelineLayout,
  iconForTimelineLayout,
  labelForTimelineLayout
} from "../../constants/timelineLayoutOptions";
import { labelForZoomLimit, zoomLimits } from "../../constants/zoomRange";
import dialogs from "../../dialogs";
import narrativeViewSelectors from "../../features/narrativeView/selectors";
import timelineViewSelectors from "../../features/timelineView/selectors";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { definitionOperations, definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { isOngoingAllowedForCalendar } from "../../modules/definition/calendar/selectors";
import { mindmapViewSettingsOperations } from "../../modules/definition/mindmapViewSettings";
import { narrativeViewSettingsOperations } from "../../modules/definition/narrativeViewSettings";
import {
  relationshipViewSettingsOperations,
  relationshipViewSettingsSelectors
} from "../../modules/definition/relationshipViewSettings";
import { subwayViewSettingsOperations, subwayViewSettingsSelectors } from "../../modules/definition/subwayViewSettings";
import { timelineViewSettingsOperations } from "../../modules/definition/timelineViewSettings";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { mindmapOperations } from "../../modules/mindmaps";
import { preferencesSelectors } from "../../modules/preferences";
import { timelineRangeOperations, timelineRangeSelectors } from "../../modules/timelineRange";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { ItemCardViewSettingsRecord } from "../../types/immutableRecords/model/definitions/ItemCardViewSettingsRecord";
import { mergeDispatchToProps, wrapEditingComponent } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  canLimitDateRangeToItems,
  getOnExtendRangeToItems,
  getOnLimitDateRangeToItems
} from "../../utils/timelineRanges";
import { getViewSettingsAndOperationsOfActiveViewType } from "../../utils/viewSettings";
import viewUtils from "../../utils/viewUtils";
import { getShouldShowRelationshipOptions } from "../settingsModal/utils";

const isReadonly = preprocessorQuery.isReadonly();

const optionForFontPercentage = (percentage, selectedFontPercentage) => {
  return immutableRecords.PopoverMenuItemRecord({
    id: String(percentage),
    label: `${percentage}%`,
    state: percentage === selectedFontPercentage ? checkboxStates.CHECKED : checkboxStates.UNCHECKED
  });
};

const fontPercentageOptions = fontPercentage => {
  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children: List(_.map(fontSizes.availablePercentages, p => optionForFontPercentage(p, fontPercentage)))
    })
  ]);
};

const popoverMenuItem = (value, selectedValue, labels, descriptions, icons) => {
  return immutableRecords.PopoverMenuItemRecord({
    id: String(value),
    label: labels(value),
    description: descriptions ? descriptions(value) : null,
    icon: icons ? icons(value) : null,
    state: value === selectedValue ? checkboxStates.CHECKED : checkboxStates.UNCHECKED
  });
};

const timelineLayoutOptionsList = selectedValue => {
  const timelineLayouts = [
    timelineLayoutOptions.COMPACT,
    timelineLayoutOptions.WATERFALL,
    timelineLayoutOptions.CLEAR_ALL
  ];
  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children: List(
        timelineLayouts.map(z =>
          popoverMenuItem(z, selectedValue, labelForTimelineLayout, descriptionForTimelineLayout, iconForTimelineLayout)
        )
      )
    })
  ]);
};

const subwayLayoutOptions = selectedValue => {
  const subwayLayouts = [subwayDisplayOptions.SUBWAY, subwayDisplayOptions.SPLIT];
  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children: List(
        subwayLayouts.map(z =>
          popoverMenuItem(z, selectedValue, labelForSubwayLayout, descriptionForSubwayLayout, iconForSubwayLayout)
        )
      )
    })
  ]);
};

const subwayNumTextLinesOptions = selectedValue => {
  const options = [
    subwayTextLineCountOptions.ALL_LINES,
    subwayTextLineCountOptions.THREE_LINES,
    subwayTextLineCountOptions.TWO_LINES,
    subwayTextLineCountOptions.ONE_LINE,
    subwayTextLineCountOptions.NO_LINES
  ];
  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children: List(
        options.map(z =>
          popoverMenuItem(z, selectedValue, labelForSubwayTextLineCount, null, iconForSubwayTextLineCount)
        )
      )
    })
  ]);
};

const narrativeColumnLevelOptions = selectedValue => {
  return List([
    immutableRecords.PopoverMenuGroupRecord({
      children: List(
        narrativeColumnLevels.map(z =>
          popoverMenuItem(z, selectedValue, labelForNarrativeColumnLevel, descriptionForNarrativeColumnLevel)
        )
      )
    })
  ]);
};

const zoomOptions = zoomLimits.map(l => ({
  label: labelForZoomLimit(l),
  value: l
}));

const timelineZoomPopover = useProps => {
  return (
    <ToolbarPopoverList label="Zoom Range">
      <FormDropdown label="From" options={zoomOptions} value={useProps.zoomFrom} onChange={useProps.onZoomFromChange} />
      <FormDropdown label="To" options={zoomOptions} value={useProps.zoomTo} onChange={useProps.onZoomToChange} />
    </ToolbarPopoverList>
  );
};

const timelineZoomToButton = useProps => {
  return (
    <ToolbarPopoverButton placement="top">
      <ToolbarDropdownButton
        id="footerZoomToButton"
        isSmall={false}
        label={labelForZoomLimit(useProps.zoomTo)}
        tooltip="Change time scale limit"
      />
      {timelineZoomPopover(useProps)}
    </ToolbarPopoverButton>
  );
};

const timelineZoomFromButton = useProps => {
  return (
    <ToolbarPopoverButton placement="top">
      <ToolbarDropdownButton
        id="footerZoomFromButton"
        isSmall={false}
        label={labelForZoomLimit(useProps.zoomFrom)}
        tooltip="Change time scale limit"
      />
      {timelineZoomPopover(useProps)}
    </ToolbarPopoverButton>
  );
};

const timelineZoomControls = useProps => (
  <Fragment>
    {timelineZoomFromButton(useProps)}
    <ToolbarSlider
      width={120}
      tooltip="Change time scale on x-axis"
      {...useProps.activeViewZoom}
      logarithmic={true}
      liveChange={true}
      onChange={useProps.onZoomChange}
      pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_ZOOM}
    />
    {timelineZoomToButton(useProps)}
    <ToolbarSpacer />
  </Fragment>
);

const itemOutOfRangeRow = useProps => {
  const { left, right } = useProps.eventsOutOfRangeNumber;
  const sum = left + right;
  return sum > 0 ? (
    <InspectorRow>
      <div className="outOfRangeNumber">{sum}</div>
      {getOutOfRangeText(sum)}
    </InspectorRow>
  ) : null;
};

const limitToExistingItemsRow = useProps => {
  return canLimitDateRangeToItems(useProps) ? (
    <InspectorRow>
      <FormButton label="Limit to existing items" linkStyle={true} onClick={getOnLimitDateRangeToItems(useProps)} />
    </InspectorRow>
  ) : null;
};

const extendToIncludeAllItemsRow = useProps => {
  const { left, right } = useProps.eventsOutOfRangeNumber;
  return left > 0 || right > 0 ? (
    <InspectorRow>
      <FormButton label="Extend to include all Items" linkStyle={true} onClick={getOnExtendRangeToItems(useProps)} />
    </InspectorRow>
  ) : null;
};

const timelineDateRangePopover = (useProps, onToggleCalendar) => {
  return (
    <PopoverResizableBody>
      <ToolbarPopoverList label="Date Range">
        {itemOutOfRangeRow(useProps)}
        <InspectorRow label="From">
          <InspectorField>
            <DateRangeDatePicker
              date={useProps.dateFrom}
              onDateChange={useProps.onDateFromChange}
              calendar={useProps.calendar}
              onToggleCalendarPicker={onToggleCalendar}
              label="From"
            />
          </InspectorField>
        </InspectorRow>
        <InspectorRow label="To">
          <InspectorField>
            <DateRangeDatePicker
              date={useProps.dateTo}
              onDateChange={useProps.onDateToChange}
              calendar={useProps.calendar}
              onToggleCalendarPicker={onToggleCalendar}
              label="To"
            />
          </InspectorField>
        </InspectorRow>
        {limitToExistingItemsRow(useProps)}
        {extendToIncludeAllItemsRow(useProps)}
      </ToolbarPopoverList>
    </PopoverResizableBody>
  );
};

const getDateRangeLabel = (value, errorNumber) => (
  <div
    className={`dateRangeLabel${errorNumber > 0 ? " error" : ""}${value === specialSyntax.INFINITY ? " infinity" : ""}`}
  >
    {value}
  </div>
);

const DATE_RANGE_STRING_OPTION = {
  dateFormat: DateFormatIdentifiers.Short,
  includeDayName: false
};

const getDateRangeButtonLabel = useProps => {
  const { calendar, dateFrom, dateTo, eventsOutOfRangeNumber } = useProps;
  let content = null;
  if (isDateValid(dateFrom) || isDateValid(dateTo)) {
    const from = isDateValid(dateFrom)
      ? stringFromDate(calendar, dateFrom, DATE_RANGE_STRING_OPTION)
      : specialSyntax.INFINITY;
    const to = isDateValid(dateTo)
      ? stringFromDate(calendar, dateTo, DATE_RANGE_STRING_OPTION)
      : specialSyntax.INFINITY;
    content = (
      <Fragment>
        {getDateRangeLabel(from, eventsOutOfRangeNumber.left)} - {getDateRangeLabel(to, eventsOutOfRangeNumber.right)}
      </Fragment>
    );
  } else {
    content = interfaceMessages.NO_DATE_RANGE;
  }

  return <div className="dateRangeLabelLine">{content}</div>;
};

const dateRangeButtonTooltip = isReadonly ? "Scrollable date range" : "Change scrollable date range";

const timelineDateRangeButton = (useProps, onToggleCalendar, isDateRangeCalendarOpen) => {
  return (
    <ToolbarPopoverButton placement="top" hasChildrenPopoverOpen={isDateRangeCalendarOpen} disabled={isReadonly}>
      <ToolbarDropdownButton
        id="footerDateRangeButton"
        isSmall={false}
        label={getDateRangeButtonLabel(useProps)}
        tooltip={dateRangeButtonTooltip}
        pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_RANGE}
        disabled={isReadonly}
      />
      {timelineDateRangePopover(useProps, onToggleCalendar)}
    </ToolbarPopoverButton>
  );
};

const timelineDateRangeControls = (useProps, onToggleCalendar, isDateRangeCalendarOpen) => (
  <Fragment>
    {timelineDateRangeButton(useProps, onToggleCalendar, isDateRangeCalendarOpen)}
    <ToolbarSpacer />
  </Fragment>
);

const timelineLayout = useProps => (
  <ToolbarGroup position="right" flex={0}>
    <ToolbarPopoverButton placement="top-start">
      <ToolbarIconButton
        id="footerTimelineLayoutButton"
        isSmall={false}
        active={false}
        tooltip={"Choose layout algorithm"}
        text={labelForTimelineLayout(useProps.eventLayout)}
        icon={iconForTimelineLayout(useProps.eventLayout)}
        pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_LAYOUT}
      />
      <ToolbarPopoverList
        label="Timeline layout"
        groups={timelineLayoutOptionsList(useProps.eventLayout)}
        showSelectedIcon={true}
        onClick={useProps.onEventLayoutChange}
        showDescription={true}
        showIcons={true}
      />
    </ToolbarPopoverButton>
  </ToolbarGroup>
);

const timelineDependencyLines = useProps => (
  <ToolbarGroup position="right" flex={0}>
    <ToolbarIconButton
      icon="footerTimelineDependencyLines"
      active={useProps.showDependencies}
      onClick={() => useProps.onShowDependenciesChange(!useProps.showDependencies)}
      tooltip="Show dependency lines"
      pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_SHOW_DEPENDENCIES}
    />
  </ToolbarGroup>
);

const timelineTodayLine = useProps => (
  <ToolbarGroup position="right" flex={0}>
    <ToolbarIconButton
      icon="footerTimelineTodayLine"
      active={useProps.showTodayLine}
      onClick={() => useProps.onShowTodayLineChange(!useProps.showTodayLine)}
      tooltip="Show today line"
      pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_SHOW_TODAY}
    />
  </ToolbarGroup>
);

const timelineTypes = useProps => {
  let checkboxRows = [];
  for (let [typeId, type] of useProps.typesById) {
    const isChecked = useProps.timelineViewSettings.showTypes.indexOf(typeId) >= 0;
    if (type.canHaveDate) {
      checkboxRows.push(
        <ModalField key={type.id}>
          <FormCheckbox
            key={type.id}
            label={type.label}
            checkboxState={isChecked}
            onChange={newState => useProps.onShowTypeChange(type.id, newState)}
          />
        </ModalField>
      );
    }
  }

  if (!checkboxRows.length) {
    return null;
  }
  return (
    <ToolbarGroup position="right" flex={0}>
      <ToolbarPopoverButton placement="top-start">
        <ToolbarIconButton
          id="footerTimelineTypesButton"
          icon="footerTimelineTypes"
          active={false}
          tooltip="Choose types to display on timeline"
          pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_SHOW_TYPES}
        />
        <div className="toolbarPopoverListGroup toolbarPopoverCheckboxGroup" onClick={() => {}}>
          <div className="toolbarPopoverHeading">Show types on timeline</div>
          {checkboxRows}
        </div>
      </ToolbarPopoverButton>
    </ToolbarGroup>
  );
};

const timelineGroups = useProps => {
  return (
    <ToolbarGroup position="right" flex={0}>
      <ToolbarPopoverButton placement="top-start">
        <ToolbarIconButton
          id="footerTimelineGroupButton"
          isSmall={false}
          active={useProps.timelineGroupByEnabled}
          icon="footerTimelineGroupBy"
          tooltip={`Divide timeline into stacked groups`}
          pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_TIMELINE_GROUP_BY}
        />
        <div className="subwaySettingsPopoverContent">
          <div className={`toolbarPopoverHeading`}>Group items in timeline</div>
          <div className={`toolbarPopoverExplanation`}>Applies to focused view only</div>
          <TreeListView
            groups={useProps.timelineAllowedGroups}
            onChange={(id, showGroup) => useProps.onTimelineGroupToggle(useProps.activeViewId, id, showGroup)}
          />
        </div>
      </ToolbarPopoverButton>
    </ToolbarGroup>
  );
};

const subwayLayout = useProps => (
  <ToolbarPopoverButton placement="top-start">
    <ToolbarTextButton
      id="footerSubwayLayoutButton"
      isSmall={false}
      text={labelForSubwayLayout(useProps.subwayDisplay)}
      icon={
        useProps.subwayDisplay === subwayDisplayOptions.SUBWAY
          ? "footerSubwayLayoutSubway"
          : "footerSubwayLayoutSeparate"
      }
      active={true}
      tooltip="Choose subway layout algorithm"
    />
    <ToolbarPopoverList
      label="Subway layout"
      groups={subwayLayoutOptions(useProps.subwayDisplay)}
      showSelectedIcon={true}
      onClick={value => {
        useProps.onSubwayDisplayChange(value);
      }}
      showDescription={true}
      showIcons={true}
    />
  </ToolbarPopoverButton>
);

const subwayItemLabelHeight = useProps => {
  return (
    <ToolbarGroup position="right" flex={0}>
      <ToolbarPopoverButton placement="top-start">
        <ToolbarIconButton
          id="footerSubwayLineCount"
          isSmall={false}
          active={false}
          icon={iconForSubwayTextLineCount(useProps.subwayNumLines)}
          tooltip={"Select max text lines in label"}
        />
        <ToolbarPopoverList
          label="Show Text"
          groups={subwayNumTextLinesOptions(useProps.subwayNumLines)}
          showSelectedIcon={true}
          onClick={value => {
            useProps.onSubwayLineCountChange(value);
          }}
          showDescription={true}
          showIcons={true}
        />
      </ToolbarPopoverButton>
    </ToolbarGroup>
  );
};

const narrativeColumnLevel = useProps => (
  <ToolbarPopoverButton placement="top-start">
    <ToolbarTextButton
      id="footerNarrativeColumnsButton"
      isSmall={false}
      text={shortLabelForNarrativeColumnLevel(useProps.narrativeColumnLevel)}
      icon="footerNarrativeColumns"
      tooltip="Select layout algorithm"
    />
    <ToolbarPopoverList
      label="Arrange items horizontally"
      groups={narrativeColumnLevelOptions(useProps.narrativeColumnLevel)}
      showSelectedIcon={true}
      onClick={value => {
        useProps.onNarrativeColumnLevelChange(Number(value));
      }}
      showDescription={true}
      showIcons={false}
    />
  </ToolbarPopoverButton>
);

const mindmapRelationshipLabels = useProps => (
  <ToolbarIconButton
    icon="footerMindmapRelationshipLabels"
    active={useProps.mindmapViewSettings.showConnectorLabels}
    onClick={() => useProps.onShowMindmapRelationshipLabels(!useProps.mindmapViewSettings.showConnectorLabels)}
    tooltip="Show relationship labels"
  />
);

// const relationshipEntityColorComponent = useProps => (
//   <ToolbarGroup position="right" flex={0}>
//     <ToolbarIconButton
//       icon="footerRelationshipColumnColor"
//       active={useProps.relationshipShowEntityColor}
//       onClick={() => useProps.onShowEntityColorChange(!useProps.relationshipShowEntityColor)}
//       tooltip="Show entity column color"
//     />
//   </ToolbarGroup>
// );

const lockScrolling = useProps => (
  <ToolbarGroup position="right" flex={0}>
    {useProps.scrollingIsLocked ? (
      <ToolbarIconButton
        icon="footerScrollLock"
        active={true}
        onClick={useProps.onUnlockClick}
        tooltip="Unlock scrolling"
        pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_LOCK_SCROLLING}
      />
    ) : (
      <ToolbarIconButton
        icon="footerScrollLock"
        active={false}
        onClick={useProps.onLockClick}
        tooltip="Lock scrolling"
        pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_LOCK_SCROLLING}
      />
    )}
  </ToolbarGroup>
);

const deleteMindmap = (useProps, onDeleteMindmap) => {
  return useProps.activeViewType === MINDMAP_VIEW
    ? wrapEditingComponent(
      <ToolbarIconButton
        icon="toolbarTrash"
        tooltip="Delete Mindmap"
        active={false}
        onClick={onDeleteMindmap}
        editMode={true}
      />
    )
    : null;
};

const fontPercentage = useProps => (
  <ToolbarPopoverButton placement="top-start">
    <ToolbarDropdownButton
      id="footerFontSizeButton"
      label={`${useProps.fontPercentage}%`}
      tooltip="Change Workview zoom"
    />
    <ToolbarPopoverList
      groups={fontPercentageOptions(useProps.fontPercentage)}
      showSelectedIcon={true}
      onClick={useProps.onFontPercentageClick}
    />
  </ToolbarPopoverButton>
);

const getRelationshipTypesCheckboxes = useProps => {
  const { activeViewType } = useProps;
  const { operations, viewSettings } = getViewSettingsAndOperationsOfActiveViewType(useProps);
  const isTimelineView = activeViewType === TIMELINE_VIEW;
  const { showRelationshipTypes } = viewSettings;
  const disabled = isTimelineView && useProps.showMarkerOnly;
  const shouldShowRelationshipOptions = getShouldShowRelationshipOptions(activeViewType);

  return shouldShowRelationshipOptions ? (
    <div className="toolbarPopoverContentGroup">
      <ModalField>Relationships</ModalField>
      {useProps.referenceTypesOfView.map(t => (
        <ModalField key={t.id}>
          <FormCheckbox
            label={t.label}
            checkboxState={showRelationshipTypes.includes(t.id)}
            onChange={_newState => useProps.onChangeCardDisplay(operations.toggleShowRelationshipTypes([t.id]))}
            disabled={disabled}
          />
        </ModalField>
      ))}
    </div>
  ) : null;
};

const itemCardDetails = useProps => {
  const narrativeRowIndex = 3;
  const isTimelineView = useProps.activeViewType === TIMELINE_VIEW;
  const isMarkerOnly = isTimelineView && useProps.showMarkerOnly;
  let rows = [...itemCardDetailRows];
  if (!useProps.includeNarrative) {
    rows.splice(narrativeRowIndex, 1);
  }

  const { operations, viewSettings } = getViewSettingsAndOperationsOfActiveViewType(useProps);

  let viewSettingsRecord = ItemCardViewSettingsRecord(viewSettings);
  if (_.isNil(viewSettings.showImage)) {
    viewSettingsRecord = viewSettingsRecord.set("showImage", false);
  }

  const onCheckboxChange = (rowId, newState) => {
    // Which operation do we want to use, based on rowId (which specifies property name)
    const row = rows.find(r => r.id === rowId);
    const op = operations[row.onChangeFunctionName];
    useProps.onChangeCardDisplay(op(newState));
  };

  const checkboxRows = (
    <div className="toolbarPopoverContentGroup">
      {_.map(rows, row => {
        const isChecked = viewSettings.get(row.id);
        return (
          <ModalField key={row.id}>
            <FormCheckbox
              label={row.label}
              checkboxState={isChecked && !isMarkerOnly}
              onChange={newState => onCheckboxChange(row.id, newState)}
              disabled={_.isNil(isChecked) || isMarkerOnly}
            />
          </ModalField>
        );
      })}{" "}
    </div>
  );

  const relationshipTypesCheckboxes = getRelationshipTypesCheckboxes(useProps);

  return (
    <ToolbarGroup position="right" flex={0}>
      <ToolbarPopoverButton placement="top-start">
        <ToolbarIconButton
          id="footerCardDetailsButton"
          icon="footerCardDetails"
          active={false}
          tooltip="Customize item card"
          pulse={useProps.pulsateIconId === interfaceIdentifiers.FOOTER_CARD_DETAILS}
        />
        <div
          className="itemCardSettingsPopoverList toolbarPopoverListGroup toolbarPopoverCheckboxGroup"
          onClick={() => {}}
        >
          <div className="toolbarPopoverHeading">Show for each item</div>
          {isTimelineView ? (
            <ModalField>
              <FormCheckbox
                label={`Marker only`}
                checkboxState={isMarkerOnly}
                onChange={useProps.onShowMarkerOnlyChange}
                disabled={false}
              />
            </ModalField>
          ) : null}
          {checkboxRows}
          {relationshipTypesCheckboxes}
          <ModalField>
            <FormButton
              rightAlign={true}
              disabled={isMarkerOnly}
              label="Apply to all views"
              linkStyle={true}
              onClick={() => {
                dialogs.okCancelPrompt(interfaceMessages.APPLY_VIEW_SETTINGS_TO_ALL_VIEWS, result => {
                  if (result) {
                    useProps.applyViewSettingsToAllViews(viewSettingsRecord);
                  }
                });
              }}
            />
          </ModalField>
        </div>
      </ToolbarPopoverButton>
    </ToolbarGroup>
  );
};

const Footer = props => {
  const isTimelineView = props.activeViewType === TIMELINE_VIEW;
  const showZoom = props.showZoom;
  const showCardDetails = viewUtils.getShouldShowViewSettings(props.activeViewType);

  const [isDateRangeCalendarOpen, setIsDateRangeCalendarOpen] = useState(false);

  const onDeleteMindmap = () => {
    dialogs.okCancelPrompt("Are you sure you want to delete this mindmap?", result => {
      if (result) {
        props.onDeleteMindmap(props.activeViewMindmapId);
      }
    });
  };

  return (
    <ToolbarView className="footerToolbar">
      {/* #!if harvard !== true */}
{/* <ToolbarGroup position="left" key="left">*/}
{/* {showCardDetails ? itemCardDetails(props) : null}*/}
{/* */}
{/* {props.activeViewType === TIMELINE_VIEW ? (*/}
{/* <Fragment key="timeline">*/}
{/* {timelineLayout(props)}*/}
{/* {timelineTypes(props)}*/}
{/* {timelineGroups(props)}*/}
{/* {timelineDependencyLines(props)}*/}
{/* {isOngoingAllowedForCalendar(props.calendar) ? timelineTodayLine(props) : null}*/}
{/* </Fragment>*/}
{/* ) : null}*/}
{/* */}
{/* {props.activeViewType === SUBWAY_VIEW ? (*/}
{/* <Fragment key="subway">*/}
{/* {subwayItemLabelHeight(props)}*/}
{/* {subwayLayout(props)}*/}
{/* </Fragment>*/}
{/* ) : null}*/}
{/* */}
        {/* {props.activeViewType === RELATIONSHIP_VIEW ? (
          <Fragment key="relationship">{relationshipEntityColorComponent(props)}</Fragment>
        ) : null} */}
{/* */}
{/* {props.activeViewType === NARRATIVE_VIEW ? narrativeColumnLevel(props) : null}*/}
{/* */}
{/* {props.activeViewType === MINDMAP_VIEW ? mindmapRelationshipLabels(props) : null}*/}
{/* </ToolbarGroup>*/}
      {/* #!endif */}
      <ToolbarGroup position="right" key="right">
        {isTimelineView ? timelineDateRangeControls(props, setIsDateRangeCalendarOpen, isDateRangeCalendarOpen) : null}
        {showZoom ? timelineZoomControls(props) : null}
        {/* #!if harvard !== true */}
{/* {props.activeViewType === TIMELINE_VIEW || props.activeViewType === RELATIONSHIP_VIEW*/}
{/* ? lockScrolling(props)*/}
{/* : null}*/}
        {/* #!endif */}
        {deleteMindmap(props, onDeleteMindmap)}
        {fontPercentage(props)}
      </ToolbarGroup>
    </ToolbarView>
  );
};

Footer.propTypes = {
  activeViewId: PropTypes.string.isRequired,
  activeViewMindmapId: PropTypes.string,
  scrollingIsLocked: PropTypes.bool.isRequired,
  fontPercentage: PropTypes.number.isRequired,
  activeViewZoom: PropTypes.shape({
    minValue: PropTypes.number.isRequired,
    maxValue: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  }).isRequired,
  onFontPercentageClick: PropTypes.func.isRequired,
  onLockClick: PropTypes.func.isRequired,
  onUnlockClick: PropTypes.func.isRequired,
  showZoom: PropTypes.bool.isRequired,
  zoomFrom: PropTypes.number.isRequired,
  zoomTo: PropTypes.number.isRequired,
  onZoomChange: PropTypes.func.isRequired,
  onZoomFromChange: PropTypes.func.isRequired,
  onZoomToChange: PropTypes.func.isRequired,
  dateFrom: PropTypeShapes.date,
  dateTo: PropTypeShapes.date,
  onDateFromChange: PropTypes.func.isRequired,
  onDateToChange: PropTypes.func.isRequired,
  onDateFromAndToChange: PropTypes.func.isRequired,
  timelineContentRange: PropTypes.instanceOf(immutableRecords.TimelineRangeRecord),
  // TODO: should have a PropTypeShape
  eventsOutOfRangeNumber: PropTypes.object.isRequired,
  globalFontSize: PropTypes.number.isRequired,
  // #!if harvard === true
  onScrollToDateClick: PropTypes.func.isRequired,
  // #!endif
  activeViewType: PropTypes.string,
  showTodayLine: PropTypes.bool.isRequired,
  showMarkerOnly: PropTypes.bool.isRequired,
  eventLayout: PropTypes.string.isRequired,
  showDependencies: PropTypes.bool.isRequired,
  narrativeColumnLevel: PropTypes.number.isRequired,
  subwayDisplay: PropTypes.string.isRequired,
  subwayNumLines: PropTypes.string,
  onSubwayLineCountChange: PropTypes.func,
  onShowTodayLineChange: PropTypes.func.isRequired,
  onShowMarkerOnlyChange: PropTypes.func.isRequired,
  onShowDependenciesChange: PropTypes.func.isRequired,
  onNarrativeColumnLevelChange: PropTypes.func.isRequired,
  onSubwayDisplayChange: PropTypes.func.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  narrativeViewSettings: PropTypeShapes.narrativeViewSettings.isRequired,
  timelineViewSettings: PropTypeShapes.timelineViewSettings.isRequired,
  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,
  relationshipShowEntityColor: PropTypes.bool.isRequired,
  onShowEntityColorChange: PropTypes.func.isRequired,
  mindmapViewSettings: PropTypeShapes.mindmapViewSettings.isRequired,
  applyViewSettingsToAllViews: PropTypes.func.isRequired,
  calendar: PropTypeShapes.calendar,
  typesById: ImmutablePropTypes.map.isRequired,
  pulsateIconId: PropTypes.string,
  onShowTypeChange: PropTypes.func.isRequired,
  onShowMindmapRelationshipLabels: PropTypes.func,
  timelineReferenceTypes: PropTypeShapes.listTypeDefinition,
  onDeleteMindmap: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  const activeViewType = appStateSelectors.getActiveViewType(state.present);
  const activeViewMindmapId = appStateSelectors.getViewMindmapId(state.present, activeViewId);
  const timelineViewSettings = definitionSelectors.getTimelineViewSettings(state.present);
  let showZoom = activeViewType === TIMELINE_VIEW;
  // #!if harvard === true
  showZoom = Boolean(
    appStateSelectors.getActiveViewType(state.present) === TIMELINE_VIEW &&
      !appStateSelectors.getDisableTimelineZoom(state.present)
  );
  // #!endif

  return {
    activeViewId: activeViewId,
    activeViewMindmapId,
    scrollingIsLocked: appStateSelectors.getScrollingIsLocked(state.present),
    activeViewZoom: appStateSelectors.getActiveViewZoom(state.present),
    showZoom: showZoom,
    zoomFrom: timelineViewSettings.zoomFrom,
    zoomTo: timelineViewSettings.zoomTo,
    dateFrom: timelineRangeSelectors.getRangeFrom(state.present),
    dateTo: timelineRangeSelectors.getRangeTo(state.present),
    timelineContentRange: itemSelectors.getDateRangeForTimelineContent(state.present),
    eventsOutOfRangeNumber: timelineViewSelectors.getEventsOutOfRangeNumber(state.present, activeViewId),
    timelineAllowedGroups: timelineViewSelectors.getAllowedGroupsForView(state.present, activeViewId),
    timelineGroupByEnabled: appStateSelectors.getViewTimelineGroupIds(state.present, activeViewId).size > 0,
    fontPercentage: appStateSelectors.getFontPercentage(state.present),
    globalFontSize: preferencesSelectors.getGlobalFontSize(state.present),
    activeViewType: appStateSelectors.getActiveViewType(state.present),
    showTodayLine: timelineViewSettings.showTodayLine,
    showDependencies: timelineViewSettings.showDependencies,
    showMarkerOnly: !timelineViewSettings.showItemCard,
    eventLayout: timelineViewSettings.eventLayout,
    narrativeColumnLevel: narrativeViewSelectors.getDepthForColumn(state.present),
    subwayDisplay: subwayViewSettingsSelectors.getSubwayViewSettingsDisplay(state.present),
    subwayNumLines: subwayViewSettingsSelectors.getSubwayViewSettingsNumTextLines(state.present),
    includeNarrative: definitionSelectors.isNarrativeAllowed(state.present),
    narrativeViewSettings: definitionSelectors.getNarrativeViewSettings(state.present),
    timelineViewSettings: timelineViewSettings,
    relationshipViewSettings: definitionSelectors.getRelationshipViewSettings(state.present),
    relationshipShowEntityColor: relationshipViewSettingsSelectors.getShowEntityColor(state.present),
    mindmapViewSettings: definitionSelectors.getMindmapViewSettings(state.present),
    calendar: calendarSelectors.getCalendar(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    referenceTypesOfView: typeSelectors.getReferenceTypesOfViewType(state.present, activeViewType),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present)
  };
};

const readonlyActions = {
  onFontPercentageClick: percentage => appStateOperations.setFontPercentage(Number(percentage)),
  // onRemoveClick: appStateOperations.removeView,
  onLockClick: appStateOperations.lockScrolling,
  onUnlockClick: appStateOperations.unlockScrolling,
  onZoomChange: appStateOperations.changeZoomSlider,
  onZoomFromChange: value => {
    return timelineViewSettingsOperations.changeZoomFrom(Number(value));
  },
  onZoomToChange: value => {
    return timelineViewSettingsOperations.changeZoomTo(Number(value));
  },
  // #!if harvard === true
  onScrollToDateClick: () => uiStateOperations.setMenuPopoverType(MENU_POPOVER_DATE),
  // #!endif
  onShowDependenciesChange: timelineViewSettingsOperations.changeShowDependencies,
  onShowTodayLineChange: timelineViewSettingsOperations.changeShowTodayLine,
  onShowMarkerOnlyChange: timelineViewSettingsOperations.changeShowMarkerOnly,
  onEventLayoutChange: timelineViewSettingsOperations.changeLayout,
  onNarrativeColumnLevelChange: narrativeViewSettingsOperations.setColumnLevel,
  onShowMindmapRelationshipLabels: mindmapViewSettingsOperations.changeShowConnectorLabels,
  onSubwayDisplayChange: subwayViewSettingsOperations.changeDisplay,
  onSubwayLineCountChange: subwayViewSettingsOperations.changeTextLines,
  onChangeCardDisplay: op => {
    return op;
  },
  applyViewSettingsToAllViews: definitionOperations.applyViewSettingsToAllViews,
  onShowTypeChange: (typeId, show) => timelineViewSettingsOperations.changeShowType(typeId, show),
  onShowEntityColorChange: relationshipViewSettingsOperations.changeShowEntityColor,
  onTimelineGroupToggle: (viewId, itemOrTypeId, showThread) => {
    if (itemOrTypeId === TIMELINE_GROUP_SHOW_ALL) {
      return appStateOperations.clearTimelineGroups(viewId);
    } else {
      return appStateOperations.toggleTimelineGroup(viewId, itemOrTypeId, showThread);
    }
  }
};

const editingActions = {
  onDateFromChange: value => {
    return timelineRangeOperations.changeDateFrom(value);
  },
  onDateToChange: value => {
    return timelineRangeOperations.changeDateTo(value);
  },
  onDateFromAndToChange: (dateFrom, dateTo) => {
    return batchActions([
      timelineRangeOperations.changeDateFrom(dateFrom),
      timelineRangeOperations.changeDateTo(dateTo)
    ]);
  },
  onDeleteMindmap: mindmapOperations.deleteMindmap
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
